import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import YoutubeEmbedPreview from "../../Media/YoutubeEmbedPreview";
import { gallerySecondaryCircleSurfaceSx } from "../../Gallery/galleryIconButtonStyles";
import { getYoutubeVideoIdFromUrl } from "../../../utils/string";
import { BOX_SHADOW_LITTLE, PRIMARY_COLOR_TEXT } from "../../../utils/colors";

const SelectionCheckbox = ({
  isSelected = false,
  disabled = false,
  onSelect = () => {},
}) => {
  const theme = useTheme();

  return (
    <Box
      className="gallery-checkbox"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (disabled) return;
        onSelect();
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: 34,
        height: 34,
        borderRadius: "50%",
        cursor: disabled ? "default" : "pointer",
        transition: "all 0.2s ease",
        ...gallerySecondaryCircleSurfaceSx(theme),
        "&:hover": disabled
          ? {}
          : {
              backgroundColor: `${theme.palette.button.secondary.hover.main} !important`,
            },
      }}
    >
      {isSelected ? (
        <CheckCircleIcon
          sx={{
            color: "#4caf50 !important",
            fontSize: "26px",
            "& path": {
              stroke: "#ffffff",
              strokeWidth: "3px",
              paintOrder: "stroke fill",
            },
          }}
        />
      ) : (
        <RadioButtonUncheckedIcon
          sx={{
            color: `${PRIMARY_COLOR_TEXT} !important`,
            fontSize: "28px",
          }}
        />
      )}
    </Box>
  );
};

const YoutubeGalleryGrid = ({
  items = [],
  importedUrls = new Set(),
  selectedUrls = [],
  onSelectItem = () => {},
  lastItemRef,
}) => {
  const selectedUrlSet = new Set(
    selectedUrls.map((url) => url.trim().toLowerCase())
  );

  return (
    <Grid container spacing={2} sx={{ pr: 3, pb: 8 }}>
      {items.map((item, index) => {
        const watchUrl = item.youtubeUrl || item.url;
        const youtubeVideoId = getYoutubeVideoIdFromUrl({ url: watchUrl });
        const normalizedUrl = watchUrl?.trim().toLowerCase();
        const isImported = importedUrls.has(normalizedUrl);
        const isSelected = selectedUrlSet.has(normalizedUrl);
        const isLastItem = index === items.length - 1;

        return (
          <Grid
            item
            xs={12}
            sm={6}
            lg={4}
            key={item.id || `${watchUrl}-${index}`}
            ref={isLastItem ? lastItemRef : null}
          >
            <Box
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (isImported) return;
                onSelectItem(item);
              }}
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                width: "100%",
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: BOX_SHADOW_LITTLE,
                cursor: isImported ? "default" : "pointer",
                opacity: isImported ? 0.72 : 1,
              }}
            >
              <Box
                onClick={(event) => event.stopPropagation()}
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  width: "100%",
                  flexShrink: 0,
                  px: 1,
                  py: 1,
                  minHeight: "auto",
                  borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                  backgroundColor: "#ffffff",
                }}
              >
                <SelectionCheckbox
                  isSelected={isSelected || isImported}
                  disabled={isImported}
                  onSelect={() => onSelectItem(item)}
                />
              </Box>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ pointerEvents: "none", width: "100%" }}>
                  <YoutubeEmbedPreview
                    youtubeVideoId={youtubeVideoId}
                    height={210}
                    width="100%"
                    sx={{
                      width: "100%",
                      borderRadius: 0,
                      border: "none",
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                  }}
                />
              </Box>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default YoutubeGalleryGrid;
